// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { getIntegration } from '@automator/features/page-template-library/utilities';

// Import global utils
import { openWindow } from '@automator/shared/utils/open-window';
import { isEmpty } from '@automator/shared/utils/is-empty';
import { addParametersToURL } from '@automator/shared/utils/add-parameters-to-url';
import { addParametersUTM } from '@automator/shared/utils/add-parameters-utm-to-url';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, sprintf } from '@wordpress/i18n';

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-template-sidebar-requirement' )
export class AutomatorTemplateSidebarRequirement extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * The ID of the associated integration.
	 *
	 * @type {Number}
	 * @property {Number} integrationID - Represents the ID of the integration.
	 * Attribute name in HTML: `integration-id`.
	 */
	@property( { type: Number, attribute: 'integration-id' } )
	accessor integrationID;

	/**
	 * A flag indicating if the template operates in standalone mode, typically used when the template library is embedded on an external site. Disables installation-related functionality and hides import buttons. Default is false.
	 *
	 * @type {Boolean} The standalone mode flag
	 */
	@property( { type: Boolean, attribute: 'standalone-mode' } )
	accessor standaloneMode = false;

	/**
	 * Renders the sidebar requirement component.
	 *
	 * @return {TemplateResult} The rendered template.
	 */
	render() {
		// Get data or show placeholder
		return html`
			<div class="requirement">
				${ this._templateIcon }
				${ this._templateName }
				${ this._templateShortDescription }
				${ this._templateActions }
			</div>
		`;
	}

	/**
	 * Renders the template icon.
	 *
	 * @return {TemplateResult} The rendered icon template.
	 */
	get _templateIcon() {
		return html`
			<div class="requirement-icon">
				<uo-icon integration="${ this.integrationDirectoryData.code }"></uo-icon>
			</div>
		`;
	}

	/**
	 * Renders the template name section.
	 *
	 * @return {TemplateResult} The rendered name template.
	 */
	get _templateName() {
		return html`
			<div class="requirement-name">
				${ this.integrationDirectoryData.name }
				${ this._templateMissingTag }
			</div>
		`;
	}

	/**
	 * Renders a "missing" tag if the required integration is not installed or if the associated app is not connected. This method helps indicate missing dependencies for the functionality of a given feature.
	 *
	 * @return {TemplateResult|undefined} The rendered missing tag or undefined if the integration is installed or the app is connected.
	 */
	get _templateMissingTag() {
		// Check if the template is in standalone mode
		if ( this.standaloneMode ) {
			return;
		}

		// Check if the integration is not missing and return a checkmark icon
		if ( ! this.isMissing ) {
			return html`
				<span class="requirement-name-checkmark">
					<uo-tooltip>
						${ sprintf(
							escapeHTML( __(
								'%1$s is ready to use!',
								'uncanny-automator'
							) ),
							this.integrationDirectoryData.name
						) }

						<uo-icon 
							slot="target"
							id="check-circle"
							icon-style="solid"
						></uo-icon>
					</uo-tooltip>
				</span>
			`;
		}

		// Create default chip content for the missing tag
		const chipContent = {
			tooltip: sprintf(
				escapeHTML( __(
					// eslint-disable-next-line max-len
					'%1$s is not detected on your site. Please ensure the plugin is installed and activated to use this recipe.',
					'uncanny-automator'
				) ),
				this.integrationDirectoryData.name
			),
			label: escapeHTML( __( 'Missing', 'uncanny-automator' ) ),
		};

		// Adjust content if this is related to an app
		if ( this.integrationDirectoryData.is_app ) {
			// Update the tooltip message to guide users to connect the app
			chipContent.tooltip = sprintf(
				escapeHTML( __(
					// eslint-disable-next-line max-len
					'%1$s is not connected to your site. Connect it now by clicking the "Connect" button below to use this recipe.',
					'uncanny-automator'
				) ),
				this.integrationDirectoryData.name
			);

			// Update the chip label to indicate the app is not connected
			chipContent.label = escapeHTML( __( 'Not connected', 'uncanny-automator' ) );
		}

		// Return a styled tooltip and chip element to indicate the issue
		return html`
			<uo-tooltip>
				${ chipContent.tooltip }

				<uo-chip 
					slot="target"
					color="warning" 
					size="small"
					filled
				>
					<uo-icon id="triangle-exclamation"></uo-icon>
					${ chipContent.label }
				</uo-chip>
			</uo-tooltip>
		`;
	}

	/**
	 * Renders the template's short description.
	 *
	 * @return {TemplateResult|undefined} The rendered description or undefined if empty.
	 */
	get _templateShortDescription() {
		const shortDescription = this.integrationDirectoryData.short_description;

		if ( isEmpty( shortDescription ) ) {
			return;
		}

		return html`
			<div class="requirement-description">${ shortDescription }</div>
		`;
	}

	/**
	 * Renders the template action buttons.
	 *
	 * @return {TemplateResult|undefined} The actions template.
	 */
	get _templateActions() {
		// Define what actions we're going to add
		const actions = [];

		// Check if we're not in standalone mode and the requirement is missing
		if ( ! this.standaloneMode && this.isMissing ) {
			// Check if it's an app
			if ( this.integrationDirectoryData.is_app ) {
				actions.push( this._templateButtonConnect );
			}

			// Check if it's not installed (this will only be true for integrations)
			if ( ! this.integrationDirectoryData.is_installed ) {
				actions.push( this._templateButtonInstall );
			}
		}

		// Add a learn more button for all cases
		actions.push( this._templateButtonLearnMore );

		// Check if there are no actions to show
		if ( ! actions.length ) {
			return;
		}

		return html`
			<div class="requirement-actions">
				${ actions }
			</div>
		`;
	}

	/**
	 * Returns a template button for installing the required plugin.
	 *
	 * @return {TemplateResult} The template for the install button.
	 */
	get _templateButtonInstall() {
		// Get the URL to install the plugin
		const installPluginURL = addParametersToURL(
			UncannyAutomatorBackend.templateLibrary.pluginInstallURL,
			{
				s: this.integrationDirectoryData.name
			}
		);

		return html`
			<uo-button
				size="extra-small"
				@click=${ () => {
					openWindow( {
						url: installPluginURL,
						w: 550,
						h: 600,
						onClose: () => {
							// Refresh the page after the window is closed
							location.reload();
						}
					} );
				} }
			>
				${ escapeHTML( __( 'Get plugin', 'uncanny-automator' ) ) }
			</uo-button>
		`;
	}

	/**
	 * Returns a template button for connecting the required app
	 *
	 * @return {TemplateResult} The template for the connect button.
	 */
	get _templateButtonConnect() {
		// Get the URL to connect the app, with the tabs hidden
		const connectURL = addParametersToURL(
			this.integrationDirectoryData.miscellaneous?.url_settings_page,
			{
				automator_minimal: 1,
				automator_hide_settings_tabs: 1
			}
		);

		return html`
			<uo-button
				size="extra-small"
				@click=${ () => {
					openWindow( {
						url: connectURL,
						w: 550,
						h: 600,
						onClose: () => {
							// Refresh the page after the window is closed
							location.reload();
						}
					} );
				} }
			>
				${ escapeHTML( __( 'Connect', 'uncanny-automator' ) ) }
			</uo-button>
		`;
	}

	/**
	 * Returns a template button for linking to an external page with more information.
	 *
	 * @return {TemplateResult} The template for the learn more button.
	 */
	get _templateButtonLearnMore() {
		// Add UTM parameters to the URL
		const learnMoreURL = addParametersUTM(
			this.integrationDirectoryData.external_permalink,
			{
				medium: 'template_library',
				content: 'sidebar_requirement_learn_more_btn',
			}
		);

		return html`
			<uo-button
				size="extra-small"
				color="secondary"

				href=${ learnMoreURL }
				target="_blank"
			>
				${ escapeHTML( __( 'Learn more', 'uncanny-automator' ) ) }
			</uo-button>
		`;
	}

	/**
	 * Determines if the integration or app is missing or not properly connected.
	 *
	 * This checks:
	 * - For integrations: Whether the integration is installed.
	 * - For app-based integrations: Whether the app is connected.
	 *
	 * @return {Boolean} `true` if the integration is missing or the app is not connected; otherwise, `false`.
	 */
	get isMissing() {
		const isInstalled = this.integrationDirectoryData.is_installed;
		const isApp = this.integrationDirectoryData.is_app;
		const isAppConnected = this.integrationDirectoryData.miscellaneous?.is_app_connected;

		// Check missing conditions explicitly
		const isIntegrationMissing = ! isApp && ! isInstalled;
		const isAppMissing = isApp && ! isAppConnected;

		return isIntegrationMissing || isAppMissing;
	}

	/**
	 * Returns the integration data
	 *
	 * @return {Object} The integration data
	 */
	get integrationDirectoryData() {
		return getIntegration( 'id', this.integrationID );
	}
}
